import React from 'react';

/**
 * Branded crash page rendered by the app-level Sentry.ErrorBoundary in
 * main.tsx. Before this existed, ANY render error unmounted the whole React
 * tree and left a blank page (e.g. the 2026-08 Radix empty-SelectItem crash
 * that presented as a "dead Filters button"). Plain inline styles on purpose —
 * this must render even if Tailwind/MUI themselves are what broke.
 */
/** Props Sentry's ErrorBoundary passes to its `fallback` render prop. */
interface AppErrorFallbackProps {
  eventId?: string | null;
  resetError?: () => void;
}

export default function AppErrorFallback({ eventId, resetError }: AppErrorFallbackProps) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        fontFamily: 'Inter, system-ui, sans-serif',
        padding: '24px',
      }}
    >
      <div
        style={{
          maxWidth: '460px',
          width: '100%',
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
          padding: '40px 36px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>😵</div>
        <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>
          Something went wrong
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px', lineHeight: 1.6 }}>
          The page hit an unexpected error. It's been reported automatically.
          Reloading usually fixes it. If it keeps happening, contact support and
          mention the error ID below.
        </p>
        {eventId ? (
          <p
            style={{
              fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              color: '#94a3b8',
              background: '#f1f5f9',
              borderRadius: '8px',
              padding: '8px 12px',
              margin: '0 0 24px',
              wordBreak: 'break-all',
            }}
          >
            Error ID: {eventId}
          </p>
        ) : null}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: '#0f172a',
              color: '#fff',
              border: 0,
              borderRadius: '10px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={() => {
              if (resetError) resetError();
              window.location.href = '/admin/dashboard';
            }}
            style={{
              background: '#fff',
              color: '#0f172a',
              border: '1px solid #cbd5e1',
              borderRadius: '10px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
